export function progress_width_class(percentage = 0) {
  const val = Math.min(Math.max(Number(percentage) || 0, 0), 100);
  if (val >= 100) return 'w-100';
  if (val >= 75) return 'w-75';
  if (val >= 50) return 'w-50';
  if (val >= 25) return 'w-25';
  return 'w-0';
}

export function normalize_assigned_status_entry(assignment = {}) {
  const nested_status = assignment.status || {};
  return {
    id: Number(assignment.id || 0),
    status_id: Number(
      assignment.status_id
      || assignment.workflow_status_id
      || nested_status.id
      || 0
    ),
    status_name: String(
      assignment.status_name
      || assignment.workflow_status_name
      || nested_status.status
      || nested_status.name
      || ''
    ).trim(),
    order: Number(assignment.order || 0),
    transition_action: String(assignment.transition_action || '').trim(),
  };
}

export function normalize_assigned_statuses(assignments = []) {
  return !Array.isArray(assignments)
    ? []
    : assignments
      .map(normalize_assigned_status_entry)
      .filter(a => a.id > 0 && a.status_id > 0)
      .sort((first, second) => first.order - second.order);
}

export function merge_assigned_status(statuses = [], assignment = {}) {
  const status = statuses.find((item) => String(item.id) === String(assignment.status_id));
  return status
    ? {
      ...status,
      assignmentId: assignment.id,
      order: assignment.order,
      transitionAction: assignment.transition_action || '',
    }
    : null;
}

export function is_plain_object(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function as_array(value) {
  const items = Array.isArray(value) ? value : [];
  return items.map((item) => item);
}

export function as_object(value) {
  const safe_object = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return Object.fromEntries(Object.entries(safe_object));
}

export function object_entries(value) {
  const safe_object = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  return Object.entries(safe_object).map((entry) => entry);
}

export function object_values(value) {
  return object_entries(value)
    .map((entry) => {
      const entry_items = Array.isArray(entry) ? entry : [];
      return entry_items.length > 1 ? entry_items[1] : undefined;
    })
    .filter((entry_value) => entry_value !== undefined);
}

export function is_safe_object_key(key) {
  return typeof key === 'string'
    && key !== ''
    && key !== '__proto__'
    && key !== 'prototype'
    && key !== 'constructor';
}

export function safe_get_key(source, key) {
  const source_object = as_object(source);
  if (!is_safe_object_key(key)) return undefined;
  const found_entry = object_entries(source_object).find(([entry_key]) => entry_key === key);
  const entry_items = as_array(found_entry);
  if (entry_items.length < 2) return undefined;
  const value = entry_items[1];
  return value;
}

export function safe_set(target, key, value) {
  if (!is_plain_object(target) || !is_safe_object_key(key)) return;
  Object.defineProperty(target, key, {
    configurable: true,
    enumerable: true,
    value,
    writable: true,
  });
}

export function safe_keyed_array(source, key) {
  const value = safe_get_key(source, key);
  return as_array(value);
}

export function safe_section_access_key(section_id, action_id) {
  const key = `${section_id}:${action_id}`;
  return is_safe_object_key(key) ? key : '';
}

const CONDITION_ACTION_LABELS = new Map([
  ['question_completion', 'Question Completion'],
  ['reviewer_approval', 'Reviewer Approval'],
  ['status_check', 'Previous Status Reached'],
  ['previous_status_reached', 'Previous Status Reached'],
]);

export function condition_action_names(workflow_actions, action_ids) {
  const actions = as_array(workflow_actions);
  return as_array(action_ids)
    .map((action_id) => {
      const action = actions.find((item) => String(item.id) === String(action_id));
      return String(action?.name || '').trim();
    })
    .filter(Boolean);
}

export function transition_condition_action_label(rule, workflow_actions) {
  const explicit_action = String(rule?.action || '').trim();
  if (explicit_action) return explicit_action;

  const condition_type = String(rule?.condition_type || rule?.action_type || '').trim();
  if (condition_type === 'section_action') {
    const selected_action_names = condition_action_names(workflow_actions, rule?.required_action_ids);
    return selected_action_names.length ? selected_action_names.join(', ') : 'Action Performed';
  }

  return CONDITION_ACTION_LABELS.get(condition_type) || 'Condition';
}

export function transition_condition_payload_rule(rule, action_label) {
  const selected_statuses = as_array(rule?.selected_statuses);
  return {
    action_type: String(rule?.condition_type || 'question_completion'),
    action: action_label,
    selected_actions: as_array(rule?.required_action_ids).map(Number),
    completion_type: String(rule?.required_state || 'complete'),
    status_id: Number(selected_statuses[0] || 0) || undefined,
    selected_statuses: selected_statuses.map(Number),
    section_scope: String(rule?.section_scope || 'all'),
    question_scope: String(rule?.question_scope || 'all'),
    required_role: rule?.role_required ? String(rule.role_required) : '',
    approvals_required: Number(rule?.approvals_required || 1),
    selected_sections: as_array(rule?.selected_sections).map(Number),
    selected_questions: as_array(rule?.selected_questions).map(Number),
    selected_sections_for_questions: as_array(rule?.selected_sections_for_questions).map(Number),
  };
}

function condition_rule_action_id(item, workflow_actions, resolved_action_id) {
  const explicit_action_id = String(item?.action_id || '');
  if (explicit_action_id) return explicit_action_id;

  const action = as_array(workflow_actions).find(
    (candidate) => String(candidate?.name || '').trim() === String(item?.action || '').trim()
  );
  return String(action?.id || resolved_action_id || '');
}

function condition_rule_selected_statuses(item) {
  const selected_statuses = as_array(item?.selected_statuses);
  if (selected_statuses.length) return selected_statuses.map(String);
  return item?.status_id ? [String(item.status_id)] : [];
}

function condition_rule_required_action_ids(item) {
  const required_action_ids = as_array(item?.required_action_ids);
  if (required_action_ids.length) return required_action_ids.map(String);
  return as_array(item?.selected_actions).map(String);
}

function condition_rule_scope(explicit_scope, selected_items) {
  return String(explicit_scope || (as_array(selected_items).length ? 'specific' : 'all'));
}

function condition_rule_completion_type(item) {
  return String(item?.completion_type || item?.required_state || 'complete');
}

function editable_condition_rule_arrays(item) {
  const selected_sections = as_array(item?.selected_sections);
  const selected_questions = as_array(item?.selected_questions);
  return {
    selected_sections,
    selected_questions,
    selected_sections_for_questions: as_array(item?.selected_sections_for_questions),
  };
}

function editable_condition_rule_identity(item, index) {
  return {
    id: item?.id || `condition-${index + 1}`,
    condition_type: String(item?.condition_type || item?.action_type || 'question_completion'),
    action: String(item?.action || ''),
    action_type: String(item?.action_type || ''),
  };
}

export function editable_condition_rule(item, index, workflow_actions, resolved_action_id) {
  const rule_arrays = editable_condition_rule_arrays(item);
  const rule_identity = editable_condition_rule_identity(item, index);

  return {
    ...rule_identity,
    action_id: condition_rule_action_id(item, workflow_actions, resolved_action_id),
    required_action_ids: condition_rule_required_action_ids(item),
    completion_type: condition_rule_completion_type(item),
    selected_statuses: condition_rule_selected_statuses(item),
    selected_sections_for_questions: rule_arrays.selected_sections_for_questions.map(String),
    required_state: String(item?.required_state || 'complete'),
    section_scope: condition_rule_scope(item?.section_scope, rule_arrays.selected_sections),
    question_scope: condition_rule_scope(item?.question_scope, rule_arrays.selected_questions),
    role_required: String(item?.role_required || item?.required_role || ''),
    approvals_required: Number(item?.approvals_required || 1),
    selected_sections: rule_arrays.selected_sections.map(String),
    selected_questions: rule_arrays.selected_questions.map(String),
    collapsed: false,
  };
}

function status_match_ids(status) {
  return [
    Number(status?.assignmentId || 0),
    Number(status?.assignedFormStatusId || 0),
    Number(status?.id || 0),
    Number(status?.status_id || 0),
  ].filter((id) => id > 0);
}

function transition_matches_status_insight(transition, ids, name, direction) {
  const transition_id = direction === 'source'
    ? Number(transition?.sourceId || 0)
    : Number(transition?.targetId || 0);
  const transition_name = direction === 'source'
    ? String(transition?.source || '').trim().toLowerCase()
    : String(transition?.target || '').trim().toLowerCase();
  return ids.includes(transition_id) || transition_name === name.toLowerCase();
}

function is_terminal_status(status, terminal_name_check, name) {
  return Boolean(status?.isFinal || status?.is_final || status?.persistedFinal) || terminal_name_check(name);
}

export function workflow_status_insight(status, index, transitions, terminal_name_check) {
  const ids = status_match_ids(status);
  const name = String(status?.name || status?.status_name || '').trim();
  const outgoing = as_array(transitions).filter((transition) =>
    transition_matches_status_insight(transition, ids, name, 'source')
  );
  const incoming = as_array(transitions).filter((transition) =>
    transition_matches_status_insight(transition, ids, name, 'target')
  );
  const terminal = is_terminal_status(status, terminal_name_check, name);
  const has_transitions = as_array(transitions).length > 0;

  return {
    name,
    outgoing: outgoing.length,
    incoming: incoming.length,
    terminal,
    unreachable: has_transitions && !incoming.length && index > 0,
    noOutgoingWarning: has_transitions && !outgoing.length && !terminal,
  };
}

export function section_access_entry_values(entry) {
  const section_id = Number(
    entry?.section_id
    || entry?.form_section?.section?.id
    || entry?.form_section?.section_id
    || entry?.form_section?.section?.section_id
    || entry?.form_section?.id
    || entry?.form_section?.form_section_id
    || 0
  );
  const action_id = entry?.action_id != null ? Number(entry.action_id) : null;
  return { section_id, action_id };
}

export function collapsed_condition_rule(rule) {
  return {
    ...as_object(rule),
    collapsed: true,
  };
}

export function status_id_set(statuses) {
  return new Set(as_array(statuses).map((status) => String(status?.status_id)));
}

export function new_form_status_ids(current_ids, statuses, existing_ids) {
  const selected_ids = as_array(current_ids).map(String);
  const existing_status_ids = existing_ids instanceof Set ? existing_ids : new Set();
  const additional_ids = as_array(statuses)
    .map((status) => String(status?.id))
    .filter((status_id) => !existing_status_ids.has(status_id) && !selected_ids.includes(status_id));
  return [...selected_ids, ...additional_ids];
}

export function form_questions_from_sections(form_sections) {
  return as_array(form_sections).flatMap((section) => {
    const main_questions = questions_from_section(section);
    const nested_sections = Array.isArray(section?.sub_sections)
      ? section.sub_sections
      : as_array(section?.sections);
    const nested_questions = nested_sections.flatMap((sub_section) =>
      questions_from_section(sub_section, section?.id || section?.section_id)
    );
    return main_questions.concat(nested_questions);
  });
}

function questions_from_section(section_like = {}, fallback_section_id = null) {
  const section_id = Number(
    section_like.id
    || section_like.section_id
    || section_like.sub_section_id
    || fallback_section_id
    || 0
  );
  const question_sets = [
    section_like.questions,
    section_like.section_questions,
    section_like.form_section_questions
  ];
  return question_sets.flatMap((question_list) => {
    const questions = Array.isArray(question_list) ? question_list : [];
    return questions.map((q) => ({
      id: Number(q.id || q.question_id || 0),
      label: q.label || q.question || q.question_text || `Question ${q.id || q.question_id || ''}`,
      section_id
    }));
  });
}

export function flatten_error_strings(value) {
  if (typeof value === 'string') {
    return [value];
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => flatten_error_strings(item));
  }
  if (is_plain_object(value)) {
    return object_values(value).flatMap((item) => flatten_error_strings(item));
  }
  return [];
}
